<script setup lang="ts">
import translaters from "@/tradutors/assine";
import { isMobile } from "@/shared/utils/mobile";

import api from "@/api";

const route = useRoute();

const canonical = useCanonicalUrl();

definePageMeta({
  middleware: ["not-found-lang"],
});

const reactiveLocale = computed(() => route.params.language); // Certifique-se de que locale é uma ref

const translate = computed(() => {
  if (reactiveLocale.value === "pt-br") {
    return translaters.portugues;
  } else {
    return translaters.ingles;
  }
});

const seo = await useSeoConfig(String(route.name), String(route.params.language));

// Busca os planos/preços já no SSR para que os cards venham no HTML servido
// (antes só carregavam no cliente via onBeforeMount do layout). A key inclui
// language + coupon + voucher + code_share para re-executar quando qualquer um
// muda; o resultado vai no payload do Nuxt (sem re-fetch no cliente). O guard do
// onBeforeMount no layout (!storePlan.plansType.length) pula o double-fetch.
const plansParams = resolvePlansTypeParams();
const plansKey = `assine-plans-type:${plansParams.language}:${plansParams.coupon}:${plansParams.voucher}:${plansParams.code_share}`;
await useAsyncData(plansKey, () => fetchAndSeedPlansType(plansParams));

useHead({
  templateParams: {
    subTitle: null,
    subPage: seo.value?.title || (route.params.language == "pt-br" ? "Assine" : "Subscribe Now"),
    page_url: canonical,
    page_description:
      seo.value?.description ||
      (route.params.language == "pt-br"
        ? `Quer ver tudo sem censura? Assine agora e tenha acesso ao conteúdo VIP`
        : `Want to see everything uncensored? Subscribe now and get access to VIP content`),
    page_image: shareImage(seo.value?.image),
    page_keywords:
      seo.value?.keywords ||
      "bella, bella da semana, bela da semana, bella semana, bela semana, belladasemana, bella site, bella da semana site",
    page_locale: route.params.language as string,
    page_url_pt: "https://belladasemana.com.br/pt-br/assine",
    page_url_en: "https://belladasemana.com.br/en/assine",
    page_canonical: canonical,
  },
});

const order = ref(false);
const storeUser = userStore();
const storePlan = planStore();

onMounted(() => {
  // Palpitão winners arrive via /assine?coupon_discount=CODE — pre-seed the
  // store so when they pick a plan, AssinePlansChoice auto-validates the
  // coupon (its onMounted calls validaCupomDisconto when cupomDesconto is
  // already set). Persisted in sessionStorage by the planStore plugin.
  const couponFromQuery = (route.query.coupon_discount as string) || "";
  if (couponFromQuery && !storePlan.cupomDesconto) {
    storePlan.setCupomDesconto(couponFromQuery);
  }

  //verifica se o usuario esta logado depois valida se tem assinatura ativa
  if (storeUser.isLoggedIn) getOrderActiveUser();
});

function getOrderActiveUser() {
  api
    .get(`auth/isOrderActiveUser?locale=${route.params.language ?? ""}`)
    .then(({ data: { orderActive } }) => {
      order.value = orderActive;
    });
}
</script>

<template>
  <NuxtLayout #="{ showModalMessages }">
    <div class="slot-body">
      <h1 class="visually-hidden-seo">{{ translate.seoH1 }}</h1>
      <div class="container col-lg-10 col-12 d-flex">
        <div class="breadcrumb breadcrumb2">{{ translate.breadcrumb0 }}</div>
        <div class="breadcrumb">></div>
        <div class="breadcrumb">
          {{ isMobile() ? translate.breadcrumbMobile2 : translate.breadcrumb2 }}
        </div>
        <div class="breadcrumb">></div>
        <div class="breadcrumb">
          {{ isMobile() ? translate.breadcrumbMobile3 : translate.breadcrumb3 }}
        </div>
        <div class="breadcrumb">></div>
        <div class="breadcrumb">{{ translate.breadcrumb4 }}</div>
      </div>
      <div
        class="container assine-intro-header"
        v-if="!storeUser.isActiveUser && !storeUser.isAdminUser"
      >
        <div class="header1">
          <span class="d-block header1">{{ translate.assineTitle1 }}</span>
          <span class="d-block header1">{{ translate.assineTitle2 }}</span>
        </div>
        <div class="header2">{{ translate.bePart }}</div>
      </div>
      <div class="container py-3" v-if="storeUser.isAdminUser">
        <div class="header1">
          {{
            reactiveLocale === 'pt-br'
              ? 'Você está logado como administrador.'
              : 'You are logged in as administrator.'
          }}
        </div>
      </div>
      <div class="container py-2">
        <AssineNavbarPlansOrCreditsVue
          :order="order"
          :translate="translate"
          @showModalMessages="showModalMessages"
        />
      </div>
    </div>
  </NuxtLayout>
</template>

<style scoped lang="css">
.slot-body {
  margin-top: 65px !important;
  background-color: #fff;
}

/* H1 de SEO: renderizado no SSR, mas oculto visualmente (não usar
   display:none para não ser ignorado por crawlers). */
.visually-hidden-seo {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.header1 {
    font-family: 'Roboto-Medium';
    font-weight: 900;
    font-size: 35px;
    line-height: 100%;
    color: #ff6000;    
    letter-spacing: 2px;
    text-align: center;
}

.header2 {
    font-family: 'Roboto-Medium';
    font-weight: 900;
    font-size: 33px;
    line-height: 100%;
    color: #000000;    
    letter-spacing: 2px;    
    margin-top: 10px;
    text-align: center;
}

.breadcrumb {
  padding: 20px 5px;
  font-family: "Exo-2";
  font-weight: 400;
  font-size: 14px;
  line-height: 16.8px;
  color: #979797;
  margin-bottom: unset;
  cursor: pointer;
}

.breadcrumb1 {
  color: #5b5858;
  cursor: pointer;
}

.breadcrumb2 {
  color: #5b5858;
}

/* Desktop: o texto de chamada migra para a esquerda dos cards
   (AssinePlansGridDesktop). Aqui escondemos para não duplicar. O <h1> permanece
   no DOM (apenas oculto por CSS), preservando o SEO. Breakpoint alinhado ao do
   carrossel de planos (971px): até 970 mostramos o carrossel de 1 card, que não
   tem o texto à esquerda, então o título da página precisa aparecer aqui. */
@media screen and (min-width: 971px) {
  .assine-intro-header {
    display: none;
  }
}

/* regras mobile/tablet até 970px (faixa do carrossel de 1 card) */
@media screen and (max-width: 970px) {
  .breadcrumb {
    font-size: 11px;
  }
  .header1 {
    font-size: 25px;
    line-height: 35px;
    letter-spacing: 0px;
  }

  .header2 {
    font-size: 23px;
    letter-spacing: 0px;
  }
}
</style>
